import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { useFieldArray, useFormContext } from 'react-hook-form';

import { Box, Typography, styled } from '@mui/material';

import CustomItemsList from './CustomItemsList';

import useConfirmDialog from 'hooks/dialogs/useConfirmDialog';

import DeleteButton from 'components/shared/DeleteButton';
import ProfitMarginControlled from 'components/shared/ProfitMarginControlled';
import QuantityInputControlled from 'components/shared/QuantityInputControlled';

import type {
    CustomItem,
    QuotationProductsForm,
    QuotationProductsUpdateProps,
} from 'models/quotations/Quotation';
import type { QuotationProductItem } from 'models/quotations/Quotations';
import type { RowItems } from 'models/TableProps';

import { formatPrice } from 'utils/numberFormatter';
import { CustomItemsWrapper } from 'components/quotation-editor/shared/CustomItemsWrapper';

const StyledImage = styled(Box)(() => ({
    width: '80px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    img: {
        maxWidth: '100%',
    },
}));

const StyledContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    margin: '12px 0',
    gap: '12px',
}));

const StyledRow = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
}));

const StyledColumn = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
}));

const PriceContainer = styled(Box)(() => ({
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const TitleContainer = styled(Box)(() => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    marginBottom: '6px',
    gap: '12px',
    justifyContent: 'space-between',
}));

const MobileQuatationItem: React.FC<RowItems<QuotationProductItem>> = ({
    index,
    handleChange,
    handleEvent,
}) => {
    const { control, watch } = useFormContext<QuotationProductsForm>();

    const item = watch(`items.${index}`);

    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.FIELDS.QUOTATION_PRODUCTS',
    });

    const {
        fields: customs,
        append,
        remove,
    } = useFieldArray({
        control,
        name: `items.${index}.customs`,
    });

    const confirmDialog = useConfirmDialog();

    const handleAddCustom = useCallback(
        (): void =>
            append({
                name: '',
                net_price: 1,
                quantity: 1,
                tax: 27,
            }),
        [append]
    );

    const handleRemoveCustom = useCallback(
        (index) => {
            remove(index);
        },
        [remove]
    );

    const deleteItemHandle = (productId: number) => {
        confirmDialog.showDialog({
            title: t('ROW_CONTENT.DELETE_MODAL.TITLE'),
            content: t('ROW_CONTENT.DELETE_MODAL.CONTENT'),
            confirmText: t('ROW_CONTENT.DELETE_MODAL.CONFIRM'),
            cancelText: t('ROW_CONTENT.DELETE_MODAL.CANCEL'),
            onConfirm: () => handleEvent({ index, quoteItemId: productId }),
        });
    };

    const handleCustomItemsChange = (content: CustomItem[]) => {
        handleChange<QuotationProductsUpdateProps>({
            index: index,
            id: item.id,
            item: {
                id: item.id,
                customs: content,
            },
        });
    };

    return (
        <StyledContainer>
            <TitleContainer>
                <StyledColumn>
                    <Typography variant="subtitle2">{item.name}</Typography>
                    <Typography variant="caption" color="slategrey">
                        {item.sku}
                    </Typography>
                </StyledColumn>
                <DeleteButton onClick={deleteItemHandle.bind(this, item.id)} />
            </TitleContainer>
            <StyledRow>
                <StyledImage>
                    <img src={item.image} alt={item.name} />
                </StyledImage>
                <PriceContainer>
                    <StyledColumn sx={{ gap: '6px' }}>
                        <Typography>
                            {t('TABLE_HEAD.UNIT_NET_PRICE')}:
                        </Typography>
                        <Typography variant="h6">
                            {formatPrice(item.net_price)} Ft
                        </Typography>
                    </StyledColumn>
                    <StyledColumn sx={{ gap: '6px' }}>
                        <Typography textAlign="right">
                            {t('TABLE_HEAD.NET_PRICE')}:
                        </Typography>
                        <Typography color="steelblue" variant="h6">
                            {formatPrice(item.net_price * item.quantity)} Ft
                        </Typography>
                    </StyledColumn>
                </PriceContainer>
            </StyledRow>
            <StyledColumn sx={{ gap: '12px' }}>
                <Typography>{t('TABLE_HEAD.QUANTITY')}:</Typography>
                <StyledRow
                    sx={{
                        maxWidth: '200px',
                        alignItems: 'center',
                        gap: '6px',
                    }}
                >
                    <QuantityInputControlled
                        control={control}
                        name={`items.${index}.quantity`}
                        minOrderMultiplier={!!item.min_order_multiplier}
                        minOrderQuantity={
                            item.min_order_quantity
                                ? item.min_order_quantity
                                : 1
                        }
                    />
                    {t('ROW_CONTENT.QUANTITY_UNITS')}
                </StyledRow>
                <StyledColumn sx={{ gap: '6px' }}>
                    <Typography>{t('TABLE_HEAD.MARGIN')}:</Typography>
                    <ProfitMarginControlled
                        control={control}
                        name={`items.${index}.margin`}
                        net_price={item.net_price}
                        orientation="row"
                    />
                </StyledColumn>
            </StyledColumn>
            {customs && (
                <CustomItemsWrapper
                    fieldsVariant="outlined"
                    defaultCustomItems={customs}
                    handleChange={handleCustomItemsChange}
                />
            )}
            {/* <CustomItemsList
                index={index}
                items={customs}
                onAddCustom={handleAddCustom}
                onRemoveCustom={handleRemoveCustom}
            /> */}
        </StyledContainer>
    );
};

export default MobileQuatationItem;
